import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Slot group definitions (inline literals — required by the schema linter)
// ============================================================================

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'View Floor Plans' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

/**
 * Interactive Map page intro: centered heading, blurb, and CTA that opens the
 * page below the hero — same pattern as FloorPlansIntro / VirtualTourIntro.
 */
export function InteractiveMapIntro() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const cta = useSlotGroup('interactivemapintro.cta', ctaDef);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-background)] pt-[40px] pb-16 md:pb-24"
    >
      <div className="mx-auto px-3 sm:px-4 lg:px-6 text-center">
        <RichTextContent
          path="interactivemapintro.heading"
          label="Heading"
          defaultValue="<p>Explore Our Community Map</p>"
          as="h2"
          className="text-balance text-[clamp(2rem,8.8vw,3.5rem)] sm:text-[clamp(1rem,4.4vw,2.5rem)] uppercase tracking-wide leading-[1.1] mb-6"
          style={{ color: '#000', fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}
        />

        <div className="max-w-3xl mx-auto">
          <RichTextContent
            path="interactivemapintro.body"
            label="Body Text"
            as="div"
            className="font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_a]:text-primary [&_a]:underline"
          />
        </div>

        <div className="mt-6 flex justify-center">
          <EditableSlotGroup groupPath="interactivemapintro.cta" definition={ctaDef}>
            <a
              href={cta.url || '/floor-plans'}
              target={cta._target || '_self'}
              className="imi-cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all"
            >
              {cta.label || 'View Floor Plans'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Solid primary pill — matches the site-wide CTA treatment. */
        .imi-cta {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 9999px;
        }
        .imi-cta:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'InteractiveMapIntro',
  displayName: 'Interactive Map Intro',
  tokens: {
    'interactivemapintro.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Explore Our Community Map</p>',
    },
    'interactivemapintro.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_interactivemap_blurb',
      defaultValue:
        '<p>Use our interactive map to explore available homes at <span data-type="field-token" data-field="property_name"></span>. Click a unit to see layouts, pricing, and availability, then find the perfect place to call home.</p>',
    },
  },
  slotGroups: {
    'interactivemapintro.cta': ctaDef,
  },
};

(InteractiveMapIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default InteractiveMapIntro;
